import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const CATEGORY_SET_COLLECTION = 'categorySets';
const LANGUAGES = ['gu', 'hi', 'en'];

function cleanText(value) {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

function comparableText(value) {
  return cleanText(value).toLocaleLowerCase('en-IN');
}

function cleanTranslated(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : { en: value };
  return Object.fromEntries(LANGUAGES.map((language) => [language, cleanText(source?.[language])]));
}

function hasTranslatedValue(value) {
  return LANGUAGES.some((language) => cleanText(value?.[language]));
}

function titleFromValue(value) {
  return cleanText(value)
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function slugFromEnglish(value) {
  return cleanText(value)
    .toLocaleLowerCase('en-IN')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function stableHash(value) {
  let hash = 2166136261;
  for (const character of String(value)) {
    hash ^= character.codePointAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function categoryValueKey(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const translated = cleanTranslated(value);
    const primary = translated.en || translated.gu || translated.hi;
    return primary ? `translated:${comparableText(primary)}` : '';
  }
  const text = comparableText(value);
  return text ? `text:${text}` : '';
}

export function sameCategoryValue(left, right) {
  const leftKey = categoryValueKey(left);
  return Boolean(leftKey) && leftKey === categoryValueKey(right);
}

export function categoryItemFromValue(value, storage = 'translated', labelOverride) {
  if (storage === 'text') {
    const recordValue = cleanText(value);
    if (!recordValue) return null;
    const fallbackLabel = titleFromValue(recordValue);
    const label = cleanTranslated(labelOverride || {
      gu: fallbackLabel,
      hi: fallbackLabel,
      en: fallbackLabel,
    });
    const key = categoryValueKey(recordValue);
    return { id: `category-${stableHash(key)}`, label, value: recordValue };
  }

  const translated = cleanTranslated(value);
  if (!hasTranslatedValue(translated)) return null;
  const key = categoryValueKey(translated);
  return { id: `category-${stableHash(key)}`, label: translated, value: translated };
}

export function categoryItemFromLabels(labels, storage = 'translated') {
  const cleanLabels = cleanTranslated(labels);
  if (!LANGUAGES.every((language) => cleanLabels[language])) {
    throw new Error('Gujarati, Hindi ane English category name required chhe.');
  }
  if (storage === 'text') {
    const recordValue = slugFromEnglish(cleanLabels.en);
    if (!recordValue) throw new Error('English category mathi valid value generate thai nathi.');
    return categoryItemFromValue(recordValue, storage, cleanLabels);
  }
  return categoryItemFromValue(cleanLabels, storage, cleanLabels);
}

export function mergeCategoryItems(...groups) {
  const merged = new Map();
  groups.flat().filter(Boolean).forEach((item) => {
    const key = categoryValueKey(item.value);
    if (!key) return;
    const normalized = categoryItemFromValue(item.value, typeof item.value === 'string' ? 'text' : 'translated', item.label);
    if (!normalized) return;
    const current = merged.get(key);
    if (!current) {
      merged.set(key, { ...normalized, ...item, label: cleanTranslated(item.label || normalized.label) });
      return;
    }
    const nextLabel = cleanTranslated(item.label || {});
    merged.set(key, {
      ...current,
      label: Object.fromEntries(LANGUAGES.map((language) => [
        language,
        current.label?.[language] || nextLabel[language],
      ])),
    });
  });
  return [...merged.values()].sort((left, right) => {
    const leftLabel = left.label?.en || left.label?.gu || left.label?.hi || '';
    const rightLabel = right.label?.en || right.label?.gu || right.label?.hi || '';
    return leftLabel.localeCompare(rightLabel, 'en', { sensitivity: 'base' });
  });
}

export function subscribeToCategorySet(moduleKey, onData, onError) {
  return onSnapshot(doc(db, CATEGORY_SET_COLLECTION, moduleKey), (snapshot) => {
    onData(
      mergeCategoryItems(snapshot.exists() ? snapshot.data()?.items || [] : []),
      { exists: snapshot.exists() },
    );
  }, onError);
}

export async function ensureCategoryItems(moduleKey, items) {
  const incoming = mergeCategoryItems(items);
  if (!incoming.length) return [];
  const reference = doc(db, CATEGORY_SET_COLLECTION, moduleKey);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(reference);
    const current = snapshot.exists() ? snapshot.data()?.items || [] : [];
    const normalizedCurrent = mergeCategoryItems(current);
    const merged = mergeCategoryItems(current, incoming);
    if (JSON.stringify(merged) !== JSON.stringify(normalizedCurrent)) {
      transaction.set(reference, {
        items: merged,
        updatedAt: serverTimestamp(),
        ...(!snapshot.exists() && { createdAt: serverTimestamp() }),
      }, { merge: true });
    }
    return merged;
  });
}

export async function addCategoryItem(moduleKey, item) {
  const normalized = mergeCategoryItems([item])[0];
  if (!normalized) throw new Error('Valid category required chhe.');
  const reference = doc(db, CATEGORY_SET_COLLECTION, moduleKey);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(reference);
    const current = snapshot.exists() ? snapshot.data()?.items || [] : [];
    if (current.some((entry) => sameCategoryValue(entry.value, normalized.value))) {
      throw new Error('Aa category already exist kare chhe.');
    }
    const items = mergeCategoryItems(current, [normalized]);
    transaction.set(reference, {
      items,
      updatedAt: serverTimestamp(),
      ...(!snapshot.exists() && { createdAt: serverTimestamp() }),
    }, { merge: true });
    return normalized;
  });
}

export async function removeEmptyCategory(moduleKey, item, recordsCollection, fieldKey) {
  const records = await getDocs(collection(db, recordsCollection));
  const isUsed = records.docs.some((record) => sameCategoryValue(record.data()?.[fieldKey], item.value));
  if (isUsed) throw new Error('Aa category data ma use thay chhe, etle remove nahi thai shake.');

  const reference = doc(db, CATEGORY_SET_COLLECTION, moduleKey);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(reference);
    if (!snapshot.exists()) return;
    const items = (snapshot.data()?.items || [])
      .filter((entry) => !sameCategoryValue(entry.value, item.value));
    transaction.set(reference, { items, updatedAt: serverTimestamp() }, { merge: true });
  });
}
